<script setup lang="ts">
/**
 * Login.vue — 登录 / 注册 / 忘记密码（三流程，Nexus Cyberpunk 玻璃态）
 * - ?redirect= 参数：登录成功后跳回（仅允许 chrome-extension:// 前缀，供扩展回调）
 * - 邮箱验证码 UI 由 /site/login/config 开关控制
 * - 密码 AES-256-CBC 加密后提交（api/crypto.ts）
 */
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import BrandMark from '@/components/BrandMark.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import PasswordInput from '@/components/PasswordInput.vue'
import AgreementCheckbox from '@/components/AgreementCheckbox.vue'
import SliderCaptcha from '@/components/SliderCaptcha.vue'
import { useAuthStore } from '@/stores/auth'
import { useSeo } from '@/composables/useSeo'
import {
  fetchLoginConfig,
  apiLogin,
  apiRegister,
  apiSendRegisterCode,
  apiSendResetCode,
  apiResetPassword,
  apiUserState,
  apiIssueHandoff,
  apiOauthRedirect,
  type OAuthProvider
} from '@/api/auth'
import { encryptPassword, decryptPassword } from '@/api/crypto'
import type { UserInfo } from '@/types/auth'
import { useInviteCode } from '@/composables/useInviteCode'
import {
  checkEmail,
  checkPassword,
  checkCode,
  getPasswordRuleStatus
} from '@/utils/validation'
import {
  readPendingLogin,
  persistPendingIntent,
  clearPendingIntent,
  isValidExtId
} from '@/utils/pendingLogin'
import { toastSuccess, toastError, toastWarning } from '@/utils/feedback'

type Mode = 'login' | 'register' | 'forgot'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAuthStore()

const mode = ref<Mode>('login')
const emailVerifyEnabled = ref(true)

// 第三方登录（OAuth）：仅展示后端配置中已就绪的 provider
const oauthProviders = ref<OAuthProvider[]>([])
const oauthLoading = ref('')
const deviceId = ref('')

// 第三方登录品牌按钮文案：优先专用 key，缺失时回落 provider.label
const OAUTH_LABEL_KEYS: Record<string, string> = {
  qq: 'login.withQQ',
  douyin: 'login.withDouyin',
  google: 'login.withGoogle',
}
function oauthLabel(p: OAuthProvider): string {
  const key = OAUTH_LABEL_KEYS[p.key]
  return key ? t(key) : p.label
}

// 品牌标官方色（simple-icons 单色字形 + currentColor 染色）；未收录的 provider 走继承色
const OAUTH_BRAND_COLORS: Record<string, string> = {
  wechat: '#07c160',
  qq: '#12b7f5',
  douyin: '#fe2c55',
  google: '#4285f4',
  apple: '#f5f5f7',
  github: '#f5f5f7',
}
function oauthBrandColor(key: string): string {
  return OAUTH_BRAND_COLORS[key] ?? 'inherit'
}

// 邀请码：注册时把本地持久化的邀请码回传后端（绑定受邀关系 + 邀请者注册奖励）
const inviteStore = useInviteCode()
const inviteCode = computed(() => inviteStore.get())
const loading = ref(false)
const sendingCode = ref(false)
const countdown = ref(0)
const agreed = ref(false)
const rememberAccount = ref(false)
const rememberPassword = ref(false)

// ⚠️ form 必须先于下方「输入即清除字段错误」watch 声明：该 watch 的 getter 在 setup 期
// 会立即执行一次（Vue 用它取初值），此时 form 若仍在 TDZ 会抛 ReferenceError，整个页面白屏。
const form = ref({
  email: '',
  code: '',
  password: '',
  confirmPassword: '',
  name: ''
})

// 字段级校验错误（2026-09-09 提示体系重构：规则类错误就地显示，一次性状态走 toast）
const fieldErrors = reactive<{ email: string; password: string; code: string }>({
  email: '',
  password: '',
  code: ''
})
// 协议勾选错误（表单级规则，非字段；显示在协议行下方）
const agreeError = ref('')

function clearFieldErrors() {
  fieldErrors.email = ''
  fieldErrors.password = ''
  fieldErrors.code = ''
  agreeError.value = ''
}

// 输入即清除对应字段错误（规则类错误不跨输入残留）
watch(
  () => [form.value.email, form.value.password, form.value.code],
  () => clearFieldErrors()
)
watch(agreed, () => {
  agreeError.value = ''
})

// localStorage 键（与 auth store 同惯例：client-only + try/catch）
// 登录页不进搜索索引（含账号/验证码交互；SSG 预渲染产物同样带 noindex）
useSeo({ page: 'login', robots: 'noindex, nofollow' })

const PRIVACY_AGREED_KEY = 'yfb.privacy.agreed'
// 记住登录标识（2026-09 起为邮箱；旧键 yfb.login.account 存的是用户名，不再读取）
const REMEMBER_ACCOUNT_KEY = 'yfb.login.email'
const REMEMBER_PASSWORD_KEY = 'yfb.login.password'

// 待回跳意图：扩展打开登录页时写入本地，用户浏览其他页面后再回登录页
// 也能凭存储的意图完成回跳（不再依赖 URL 参数幸存）。TTL 24h。
// 意图在 setup 期同步写入（watch immediate），杜绝"用户立刻跳走导致没落盘"的竞态。
// 实现见 @/utils/pendingLogin.ts（LoginCallback.vue 共用）。

// 旧版 ?redirect=chrome-extension://<id>/tab.html（仅兼容历史扩展构建）
const legacyRedirect = computed(() => {
  const r = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  return r.startsWith('chrome-extension://') || r.startsWith('moz-extension://') ? r : ''
})

// web 内回跳目标（订阅页等站内路径被踢去登录的转化闭环，ADR-0029 链路）：
// 白名单 = 以 / 开头的本站路径；拒绝协议外链（//、http:）、扩展协议由 legacyRedirect 处理
const webRedirectPath = computed(() => {
  const r = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  if (r.startsWith('/') && !r.startsWith('//') && !r.includes(':')) return r
  return ''
})

// 本次是否来自扩展（新参数 from=extension，或兼容旧 redirect 参数）
const fromExtension = computed(() => route.query.from === 'extension' || !!legacyRedirect.value)

// 扩展 ID：优先 ?ext_id=，其次从旧 redirect 提取
const extIdFromQuery = computed(() => {
  const v = typeof route.query.ext_id === 'string' ? route.query.ext_id : ''
  return isValidExtId(v) ? v : ''
})

/** 从旧扩展回跳地址提取扩展 ID（chrome-extension://<id>/tab.html / moz-extension://<uuid>/tab.html） */
function extractExtId(redirectUrl: string): string {
  try {
    return new URL(redirectUrl).hostname
  } catch {
    return ''
  }
}

// 待回跳意图读写（readPendingLogin / clearPendingIntent / persistPendingIntent）见 @/utils/pendingLogin.ts
//（LoginCallback.vue 共用；站点登录态桥已随 ADR 0028 删除）。

// 竞态防护：setup 期即同步持久化待回跳意图（不依赖 onMounted 的 await 链）
watch([extIdFromQuery, legacyRedirect], () => {
  const extId = extIdFromQuery.value || extractExtId(legacyRedirect.value)
  const nonce = typeof route.query.ext_nonce === 'string' ? route.query.ext_nonce : ''
  persistPendingIntent(extId, nonce)
}, { immediate: true })

// setup 期读一次本地意图（放在持久化 watch 之后，读到的是含本次刚写入的那条）
const pendingIntent = readPendingLogin()

// 自动回跳判据分两档（2026-09-10）：
//   ① URL 直证 `urlProvenExtension`：from=extension（或旧 redirect）+ 合法扩展 ID + nonce 三者齐备
//      —— 扩展刚打开登录页，永远可信；
//   ② 本地意图推断：用户浏览其他页后回登录页（URL 已无参数），**仅当意图新鲜**才算数。
// 陈旧意图（上次扩展登录被放弃的残留）不得劫持纯网页登录，否则没装扩展的用户也会被送去完成页；
// 它仍可用于补齐 ext_id/nonce，但不驱动自动回跳。
const urlExtId = computed(() => extIdFromQuery.value || extractExtId(legacyRedirect.value))
const urlNonce = computed(() => (typeof route.query.ext_nonce === 'string' ? route.query.ext_nonce : ''))
const urlProvenExtension = computed(() => fromExtension.value && !!urlExtId.value && !!urlNonce.value)
const freshIntent = pendingIntent?.fresh ? pendingIntent : null

// 生效的扩展 ID / nonce / 来自扩展：URL 参数优先，其次待回跳意图（浏览其他页后回登录页场景）
const effectiveExtId = computed(() => urlExtId.value || pendingIntent?.extId || '')
const effectiveNonce = computed(() => urlNonce.value || pendingIntent?.nonce || '')
const effectiveFromExtension = computed(() => urlProvenExtension.value || !!freshIntent)

let timer: ReturnType<typeof setInterval> | null = null

// ── 「网页端已登录」确认卡（2026-09-13，ADR-0042）───────────────────────────
// 扩展发起登录、而网页端尚有有效会话时：**不再静默自动续登**。
// 用户可能想换账号 / 换登录方式（第三方），所以把选择权交回去：
//   「继续以该账号登录」→ 正常走 handoff 回跳扩展
//   「更换账号」        → 展开完整登录表单（预填当前邮箱），登录成功后**仍会**回跳扩展
//                          —— 刻意**不**清 pendingLogin：扩展发起的事实不变，
//                          换号后照样要把新账号交接给插件（清掉会让换号登录后停在网页端）。
const resumeAccount = ref<UserInfo | null>(null)
const resumeAccountLabel = computed(() => {
  const u = resumeAccount.value
  if (!u) return ''
  return u.name || u.email || ''
})

/** 用户确认沿用网页端已有账号 → 走与登录成功一致的收尾（签发 handoff 回跳扩展） */
async function continueWithExistingAccount() {
  resumeAccount.value = null
  await finishLogin()
}

/** 用户要换账号 / 换登录方式 → 丢掉确认态，展开完整登录表单（不清扩展回跳意图） */
function switchAccount() {
  const email = resumeAccount.value?.email || ''
  resumeAccount.value = null
  if (email) form.value.email = email
  switchMode('login')
}

onMounted(async () => {
  // 先恢复本地登录态:已登录用户进入登录页不应再看到登录表单
  auth.hydrate()
  try {
    const cfg = await fetchLoginConfig()
    emailVerifyEnabled.value = cfg.email_verify_enabled
    oauthProviders.value = Array.isArray(cfg.providers) ? cfg.providers : []
  } catch {
    // 配置接口失败 → 默认开启
  }
  if (typeof window === 'undefined') return
  try {
    agreed.value = localStorage.getItem(PRIVACY_AGREED_KEY) === '1'
    deviceId.value =
      (typeof route.query.device_id === 'string' ? route.query.device_id : '') ||
      localStorage.getItem('yfb.device_id') ||
      ''
  } catch {
    // 忽略
  }
  await hydrateRemember()

  // 待回跳意图已在 setup 期同步持久化（watch immediate），此处无需重复写入
  // 陈旧意图（上次扩展登录被放弃的残留）：清掉，不给后续登录留 24h 的误判隐患
  if (pendingIntent && !pendingIntent.fresh) {
    clearPendingIntent()
  }

  // 会话级一次性提示（siteClient 拦截器跳 /login?reason=expired / 踢出 reason=kicked /
  // OAuth 失败回跳 oauth_error）→ toast 一次后清理 query（保留 redirect），刷新不再重弹
  if (route.query.reason === 'expired') {
    toastWarning(t('login.sessionExpired'))
  }
  if (route.query.reason === 'kicked') {
    toastWarning(t('login.kicked'))
  }
  if (route.query.oauth_error === '1') {
    const msg = typeof route.query.oauth_error_msg === 'string' ? route.query.oauth_error_msg : ''
    toastError(msg || t('login.oauthError'))
  }
  const hasSessionNotice =
    route.query.reason === 'expired' ||
    route.query.reason === 'kicked' ||
    route.query.oauth_error === '1'
  if (hasSessionNotice) {
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : ''
    void router.replace(redirect ? { query: { redirect } } : { query: {} })
  }

  // 已登录:先向后端校验本地 token 是否仍有效（后端为账号级登录态——任意一端登出都会清除该账号
  // 全部 token，此处请求返回 401 即视为已登出，siteClient 拦截器会自动 auth.clear()）。
  // 来自扩展且已登录（token 有效）：直接 finishLogin() 自动回跳——网页端既已登录则无需重复填写
  // 账号密码；经 /login/callback 过渡页反馈，不会"点登录却毫无交互直接跳回"。
  // 仅当本地会话无效（未登录 / 401 已清）时才展示登录表单。
  if (auth.isLoggedIn) {
    try {
      // 2026-08-08：合并状态快照（顺带校验 token 有效性）
      const state = await apiUserState()
      auth.setUserInfo(state.user)
    } catch {
      // 401 → 拦截器已清本地;其他异常(网络抖动等)保守保留本地登录态
    }
    if (auth.isLoggedIn) {
      if (effectiveFromExtension.value) {
        // ⚠️ 2026-09-13（ADR-0042）：**不再静默 finishLogin() 自动续登**。
        // 旧行为是「网页端已登录 → 直接换 code 回跳扩展」，用户零点击、没有反应时间，
        // 想换账号或换登录方式（第三方）都没有入口 —— 改为渲染显式确认卡，由用户选择。
        resumeAccount.value = auth.userInfo
        return
      }
      // 非扩展访问：已登录 → 直接进个人中心（无回跳目标）
      router.replace('/account')
    }
  }
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})

function switchMode(next: Mode) {
  mode.value = next
  clearFieldErrors()
  confirmError.value = ''
}

// 协议勾选状态持久化（组件内部只负责 v-model 状态）
watch(
  agreed,
  (v) => {
    if (typeof window === 'undefined') return
    try {
      if (v) localStorage.setItem(PRIVACY_AGREED_KEY, '1')
      else localStorage.removeItem(PRIVACY_AGREED_KEY)
    } catch {
      // 忽略
    }
  }
)

function onRememberPasswordToggle(e: Event) {
  rememberPassword.value = (e.target as HTMLInputElement).checked
  if (rememberPassword.value) rememberAccount.value = true // 记住密码依赖账号回填
}

async function hydrateRemember() {
  if (typeof window === 'undefined') return
  let account = ''
  let password = ''
  try {
    account = localStorage.getItem(REMEMBER_ACCOUNT_KEY) || ''
    password = localStorage.getItem(REMEMBER_PASSWORD_KEY) || ''
  } catch {
    return
  }
  if (account) {
    rememberAccount.value = true
    form.value.email = account
  }
  if (password && account) {
    rememberPassword.value = true
    try {
      form.value.password = await decryptPassword(password)
    } catch {
      // 解密失败（密钥轮换/篡改）→ 丢弃记忆，避免填充错误
      rememberPassword.value = false
      try {
        localStorage.removeItem(REMEMBER_PASSWORD_KEY)
      } catch {
        // 忽略
      }
    }
  } else if (password && !account) {
    // 仅存密码无账号 → 丢弃
    try {
      localStorage.removeItem(REMEMBER_PASSWORD_KEY)
    } catch {
      // 忽略
    }
  }
}

async function saveRemember() {
  if (typeof window === 'undefined') return
  try {
    if (rememberAccount.value) {
      localStorage.setItem(REMEMBER_ACCOUNT_KEY, form.value.email.trim().toLowerCase())
    } else {
      localStorage.removeItem(REMEMBER_ACCOUNT_KEY)
    }
    if (rememberPassword.value && rememberAccount.value) {
      localStorage.setItem(REMEMBER_PASSWORD_KEY, await encryptPassword(form.value.password))
    } else {
      localStorage.removeItem(REMEMBER_PASSWORD_KEY)
    }
  } catch {
    // 忽略
  }
}

const passwordRules = computed(() => getPasswordRuleStatus(form.value.password))
const passwordScore = computed(() => passwordRules.value.filter((r) => r.ok).length)
const strengthLevel = computed<'weak' | 'medium' | 'strong'>(() => {
  if (passwordScore.value >= 5) return 'strong'
  if (passwordScore.value >= 3) return 'medium'
  return 'weak'
})
const strengthLabel = computed(() => {
  const map = { weak: 'login.passwordWeak', medium: 'login.passwordMedium', strong: 'login.passwordStrong' } as const
  return t(map[strengthLevel.value])
})

// 确认密码实时校验(注册/忘记密码):输入不一致立即提示,一致或未输入时清空
const confirmError = ref('')
watch(
  () => [form.value.password, form.value.confirmPassword],
  () => {
    if (!form.value.confirmPassword) {
      confirmError.value = ''
      return
    }
    confirmError.value = form.value.confirmPassword === form.value.password ? '' : t('login.passwordMismatch')
  }
)

function startCountdown(seconds = 60) {
  countdown.value = seconds
  if (timer) clearInterval(timer)
  timer = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0 && timer) {
      clearInterval(timer)
      timer = null
    }
  }, 1000)
}

function canSendCode(): boolean {
  // 注册 / 忘记密码：只需邮箱合法即可发码（忘记密码不再要求先输入新密码）
  return checkEmail(form.value.email).valid
}

/** 发送验证码前的全量校验:保证此前所有输入存在且正确,才允许进入滑块安全验证 */
function validateBeforeCode(): boolean {
  clearFieldErrors()
  if (mode.value === 'register') {
    const emailCheck = checkEmail(form.value.email)
    if (!emailCheck.valid) {
      fieldErrors.email = t(emailCheck.message)
      return false
    }
    const pwdCheck = checkPassword(form.value.password)
    if (!pwdCheck.valid) {
      fieldErrors.password = t(pwdCheck.message)
      return false
    }
    if (!form.value.confirmPassword) {
      confirmError.value = t('login.confirmPasswordPlaceholder')
      return false
    }
    if (form.value.confirmPassword !== form.value.password) {
      confirmError.value = t('login.passwordMismatch')
      return false
    }
  } else {
    // forgot：发码前置只需邮箱合法
    const emailCheck = checkEmail(form.value.email)
    if (!emailCheck.valid) {
      fieldErrors.email = t(emailCheck.message)
      return false
    }
  }
  // 协议勾选：注册 / 忘记密码发码前即要求（人为勾选，防机器批量发码）
  if (!agreed.value) {
    agreeError.value = t('login.agreeRequired')
    return false
  }
  return true
}

const captchaRef = ref<InstanceType<typeof SliderCaptcha> | null>(null)

async function handleSendCode() {
  if (sendingCode.value || countdown.value > 0) return
  // 先保证所有输入存在且正确,再进入滑块安全验证(防机器批量发码)
  if (!validateBeforeCode()) return
  captchaRef.value?.open()
}

/** 滑块验证通过后,携带一次性 ticket 真正发送验证码 */
async function sendCodeWithCaptcha(payload: { challenge_id: string; ticket: string }) {
  sendingCode.value = true
  clearFieldErrors()
  try {
    const email = form.value.email.trim().toLowerCase()
    if (mode.value === 'register') {
      await apiSendRegisterCode(email, payload.ticket)
    } else {
      await apiSendResetCode(email, payload.ticket)
    }
    toastSuccess(t('login.codeSent'))
    startCountdown()
  } catch (e) {
    toastError(extractError(e))
  } finally {
    sendingCode.value = false
  }
}

/** 第三方登录：请求后端重定向地址并跳转（扩展流程透传 ext_id/ext_nonce；device_id 取本地） */
async function handleOauth(p: OAuthProvider) {
  if (oauthLoading.value) return
  oauthLoading.value = p.key
  clearFieldErrors()
  try {
    const params: {
      web_origin: string
      ext_id?: string
      ext_nonce?: string
      device_id?: string
      invite?: string
    } = { web_origin: window.location.origin }
    // 仅扩展发起的登录才透传 ext_id/ext_nonce：未从扩展进入（URL 无直证 且 本地无新鲜待回跳意图）
    // 的纯网页 OAuth 不允许带 ext_nonce，否则 LoginCallback 会误走扩展过渡分支而不兑换 code，登录失败
    //（oauth.md §6 已知问题）。
    const isExtensionFlow = effectiveFromExtension.value
    const extId = effectiveExtId.value
    const nonce = effectiveNonce.value
    if (isExtensionFlow) {
      if (extId) params.ext_id = extId
      if (nonce) params.ext_nonce = nonce
    }
    if (deviceId.value) params.device_id = deviceId.value
    // 邀请归因（ADR-0033）：OAuth 整页跳转也带上本地邀请码，经签名 state 传递，
    // 回调注册成功时绑定邀请关系（与账密注册一致，防跳转丢码导致归因缺失）
    const invite = inviteCode.value
    if (invite) params.invite = invite
    console.log('[OAuth][handleOauth]', {
      provider: p.key,
      isExtensionFlow,
      extId: !!extId,
      extNonce: !!nonce,
      deviceId: !!deviceId.value,
      webOrigin: params.web_origin,
    })
    const res = await apiOauthRedirect(p.key, params)
    if (res && res.url) {
      window.location.href = res.url
      return
    }
    toastError(t('login.oauthError'))
  } catch {
    toastError(t('login.oauthError'))
  } finally {
    oauthLoading.value = ''
  }
}

async function handleSubmit() {
  clearFieldErrors()
  confirmError.value = ''

  if (mode.value === 'login') {
    const emailCheck = checkEmail(form.value.email)
    if (!emailCheck.valid) {
      fieldErrors.email = t(emailCheck.message)
      return
    }
    const pwdCheck = checkPassword(form.value.password)
    if (!pwdCheck.valid) {
      // 登录页不展示具体密码规则:为空 → 提示"密码为空";已输入但不符合规则 → 一律"密码不正确"
      fieldErrors.password = form.value.password ? t('login.passwordIncorrect') : t(pwdCheck.message)
      return
    }
  } else if (mode.value === 'register') {
    const emailCheck = checkEmail(form.value.email)
    if (!emailCheck.valid) {
      fieldErrors.email = t(emailCheck.message)
      return
    }
    if (emailVerifyEnabled.value) {
      const codeCheck = checkCode(form.value.code)
      if (!codeCheck.valid) {
        fieldErrors.code = t(codeCheck.message)
        return
      }
    }
    const pwdCheck = checkPassword(form.value.password)
    if (!pwdCheck.valid) {
      fieldErrors.password = t(pwdCheck.message)
      return
    }
    if (!form.value.confirmPassword) {
      confirmError.value = t('login.confirmPasswordPlaceholder')
      return
    }
    if (form.value.confirmPassword !== form.value.password) {
      confirmError.value = t('login.passwordMismatch')
      return
    }
  } else {
    const emailCheck = checkEmail(form.value.email)
    if (!emailCheck.valid) {
      fieldErrors.email = t(emailCheck.message)
      return
    }
    if (emailVerifyEnabled.value) {
      const codeCheck = checkCode(form.value.code)
      if (!codeCheck.valid) {
        fieldErrors.code = t(codeCheck.message)
        return
      }
    }
    const pwdCheck = checkPassword(form.value.password)
    if (!pwdCheck.valid) {
      fieldErrors.password = t(pwdCheck.message)
      return
    }
    if (!form.value.confirmPassword) {
      confirmError.value = t('login.confirmPasswordPlaceholder')
      return
    }
    if (form.value.confirmPassword !== form.value.password) {
      confirmError.value = t('login.passwordMismatch')
      return
    }
  }

  // 协议勾选：登录 / 注册 / 忘记密码三模式均必选（人为勾选）
  if (!agreed.value) {
    agreeError.value = t('login.agreeRequired')
    return
  }

  loading.value = true
  try {
    const encrypted = await encryptPassword(form.value.password)

    if (mode.value === 'login') {
      const res = await apiLogin(form.value.email.trim().toLowerCase(), encrypted)
      auth.setAuth(res)
      auth.persist()
      await saveRemember()
      finishLogin()
    } else if (mode.value === 'register') {
      const res = await apiRegister({
        password: encrypted,
        email: form.value.email.trim().toLowerCase(),
        code: emailVerifyEnabled.value ? form.value.code : '',
        name: form.value.name.trim(),
        invite_code: inviteCode.value || undefined
      })
      auth.setAuth(res)
      auth.persist()
      finishLogin()
    } else {
      await apiResetPassword(form.value.email.trim().toLowerCase(), encrypted, emailVerifyEnabled.value ? form.value.code : '')
      // 先弹 toast 再切模式（switchMode 会清表单错误，顺序不可反）
      toastSuccess(t('login.resetSuccess'))
      switchMode('login')
    }
  } catch (e) {
    toastError(extractError(e))
  } finally {
    loading.value = false
  }
}

async function finishLogin() {
  // web 内转化闭环：订阅页/个人中心被踢来登录的，登录成功回到原目标页
  if (webRedirectPath.value) {
    router.push(webRedirectPath.value)
    return
  }
  // 扩展发起（含浏览其他页后回登录页，凭新鲜待回跳意图）→ 换一次性 code 回跳，token 不进 URL。
  // 走 /login/callback 瞬态页（ADR-0028：登录态由扩展 SW redeem，本页仅等待扩展关标签）。
  if (effectiveFromExtension.value && effectiveExtId.value && effectiveNonce.value) {
    clearPendingIntent()
    const extId = effectiveExtId.value
    let handoffCode = ''
    // 签发失败重试一次（网络抖动），仍失败则不再走 URL 传 token（防 token 泄露）。
    // 直接进个人中心会导致扩展收不到登录态——本页 3s 兜底提示会引导用户回扩展重试。
    for (let attempt = 0; attempt < 2 && !handoffCode; attempt++) {
      try {
        const handoff = await apiIssueHandoff(extId, effectiveNonce.value)
        handoffCode = handoff?.code || ''
      } catch {
        handoffCode = ''
      }
      if (!handoffCode && attempt === 0) {
        await new Promise((r) => setTimeout(r, 500))
      }
    }
    if (handoffCode) {
      // URL 保留 ext_nonce（SW 采纳 token 前校验，防账号混淆）与 ext_id（仅备排查；网页侧不再用它拼回扩展链接）
      const query =
        `?code=${encodeURIComponent(handoffCode)}` +
        `&ext_nonce=${encodeURIComponent(effectiveNonce.value)}` +
        `&ext_id=${encodeURIComponent(extId)}`
      window.location.replace(`/login/callback${query}`)
    } else {
      // 交接登记失败（网络异常等）：token 不进 URL，直接进个人中心（扩展侧经 handoff pull 兜底）
      router.push('/account')
    }
    return
  }
  // 纯网页登录：直接进个人中心
  router.push('/account')
}

function extractError(e: unknown): string {
  const data = (e as any)?.response?.data
  if (data && typeof data.msg === 'string' && data.msg !== 'success') return data.msg
  // 走到这里 = 没有服务端 msg：请求根本没发出去（构建期密钥缺失 → sign/crypto 抛错、
  // crypto.subtle 在非安全上下文不可用等），或响应不可解析。此类异常此前被统一压成
  // 「操作失败，请稍后重试」，控制台一片安静，排查只能靠猜——留痕到控制台（2026-09-24 灰度事故）。
  console.error('[YFB][login] 请求失败且无服务端消息', e)
  return t('login.submitError')
}

// 提交按钮文案（登录/注册/忘记密码分模式，表单为主体布局）
const submitLabel = computed(() => {
  if (mode.value === 'login') return t('login.signIn')
  if (mode.value === 'register') return t('login.signUp')
  return t('login.submit')
})
</script>

<template>
  <section class="login">
    <div class="login__card">
      <div class="login__brand">
        <BrandMark size="md" variant="full" align="center" />
      </div>

      <!-- 返回登录（注册/忘记密码时顶部入口） -->
      <button
        v-if="mode !== 'login'"
        type="button"
        class="login__back"
        @click="switchMode('login')"
      >
        <YfbIcon name="lucide:arrow-left" :size="14" aria-hidden="true" />
        <span>{{ mode === 'register' ? t('login.goLogin') : t('login.backToLogin') }}</span>
      </button>

      <!-- 网页端已有会话 + 扩展发起登录 → 显式确认，不再静默自动续登（ADR-0042） -->
      <div v-if="resumeAccount" class="login__resume">
        <p class="login__resume-title">{{ t('login.resumeTitle') }}</p>
        <p class="login__resume-account">{{ resumeAccountLabel }}</p>
        <button type="button" class="login__submit" @click="continueWithExistingAccount">
          {{ t('login.resumeContinue') }}
        </button>
        <button type="button" class="login__back login__resume-switch" @click="switchAccount">
          {{ t('login.resumeSwitch') }}
        </button>
      </div>

      <form v-if="!resumeAccount" class="login__form" novalidate @submit.prevent="handleSubmit">
        <!-- 邮箱（登录 / 注册 / 忘记密码：登录标识，ADR-0041 去用户名） -->
        <label class="login__label" for="f-email">{{ t('login.email') }}</label>
        <input
          id="f-email"
          v-model="form.email"
          class="login__input"
          type="email"
          autocomplete="email"
          :placeholder="t('login.emailPlaceholder')"
        />
        <p v-if="fieldErrors.email" class="login__field-error" role="alert">{{ fieldErrors.email }}</p>
        <!-- 验证码（注册 / 忘记密码：先发码后输码） -->
        <template v-if="mode !== 'login' && emailVerifyEnabled">
          <label class="login__label" for="f-code">{{ t('login.code') }}</label>
          <div class="login__code-row">
            <input
              id="f-code"
              v-model="form.code"
              class="login__input login__code-input"
              type="text"
              inputmode="numeric"
              maxlength="6"
              autocomplete="one-time-code"
              :placeholder="t('login.codePlaceholder')"
            />
            <button
              type="button"
              class="login__code-btn"
              :disabled="sendingCode || countdown > 0 || !canSendCode()"
              @click="handleSendCode"
            >
              {{ countdown > 0 ? `${countdown}s` : t('login.sendCode') }}
            </button>
          </div>
          <p v-if="fieldErrors.code" class="login__field-error" role="alert">{{ fieldErrors.code }}</p>
        </template>
        <!-- 昵称（注册，可选；仅展示、非唯一、不可登录） -->
        <template v-if="mode === 'register'">
          <label class="login__label" for="f-name">{{ t('login.nickname') }}</label>
          <input
            id="f-name"
            v-model="form.name"
            class="login__input"
            type="text"
            autocomplete="nickname"
            :placeholder="t('login.nicknamePlaceholder')"
          />
        </template>

        <!-- 密码（登录 / 注册） -->
        <template v-if="mode !== 'forgot'">
          <label class="login__label" for="f-password">{{ t('login.password') }}</label>
          <PasswordInput
            id="f-password"
            v-model="form.password"
            :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
            :placeholder="t('login.passwordPlaceholder')"
            :error="!!fieldErrors.password"
          />
          <p v-if="fieldErrors.password" class="login__field-error" role="alert">{{ fieldErrors.password }}</p>
          <!-- 确认密码（仅注册） -->
          <template v-if="mode === 'register'">
            <label class="login__label" for="f-confirm-password">{{ t('login.confirmPassword') }}</label>
            <PasswordInput
              id="f-confirm-password"
              v-model="form.confirmPassword"
              autocomplete="new-password"
              :placeholder="t('login.confirmPasswordPlaceholder')"
              :error="!!confirmError"
            />
            <p v-if="confirmError" class="login__field-error" role="alert">{{ confirmError }}</p>
            <!-- 密码强度逐项提示（仅注册，输入后才显示） -->
            <div v-if="form.password" class="login__strength">
              <div class="login__strength-head">
                <span class="login__strength-title">{{ t('login.passwordStrength') }}</span>
                <span class="login__strength-label" :class="`login__strength-label--${strengthLevel}`">
                  {{ strengthLabel }}
                </span>
              </div>
              <div class="login__strength-track">
                <div
                  class="login__strength-fill"
                  :class="`login__strength-fill--${strengthLevel}`"
                  :style="{ width: `${(passwordScore / 5) * 100}%` }"
                />
              </div>
              <ul class="login__strength-list">
                <li
                  v-for="rule in passwordRules"
                  :key="rule.key"
                  class="login__strength-item"
                  :class="{ 'login__strength-item--ok': rule.ok }"
                >
                  <span class="login__strength-mark">{{ rule.ok ? '✓' : '✗' }}</span>
                  {{ t('validation.' + rule.key) }}
                </li>
              </ul>
            </div>
          </template>
        </template>

        <!-- 忘记密码：新密码 + 确认密码 + 强度（在邮箱与验证码之后） -->
        <template v-if="mode === 'forgot'">
          <label class="login__label" for="f-forgot-password">{{ t('login.newPassword') }}</label>
          <PasswordInput
            id="f-forgot-password"
            v-model="form.password"
            autocomplete="new-password"
            :placeholder="t('login.passwordPlaceholder')"
            :error="!!fieldErrors.password"
          />
          <p v-if="fieldErrors.password" class="login__field-error" role="alert">{{ fieldErrors.password }}</p>
          <label class="login__label" for="f-forgot-confirm-password">{{ t('login.confirmPassword') }}</label>
          <PasswordInput
            id="f-forgot-confirm-password"
            v-model="form.confirmPassword"
            autocomplete="new-password"
            :placeholder="t('login.confirmPasswordPlaceholder')"
            :error="!!confirmError"
          />
          <p v-if="confirmError" class="login__field-error" role="alert">{{ confirmError }}</p>
          <!-- 密码强度逐项提示（与注册一致，输入后才显示） -->
          <div v-if="form.password" class="login__strength">
            <div class="login__strength-head">
              <span class="login__strength-title">{{ t('login.passwordStrength') }}</span>
              <span class="login__strength-label" :class="`login__strength-label--${strengthLevel}`">
                {{ strengthLabel }}
              </span>
            </div>
            <div class="login__strength-track">
              <div
                class="login__strength-fill"
                :class="`login__strength-fill--${strengthLevel}`"
                :style="{ width: `${(passwordScore / 5) * 100}%` }"
              />
            </div>
            <ul class="login__strength-list">
              <li
                v-for="rule in passwordRules"
                :key="rule.key"
                class="login__strength-item"
                :class="{ 'login__strength-item--ok': rule.ok }"
              >
                <span class="login__strength-mark">{{ rule.ok ? '✓' : '✗' }}</span>
                {{ t('validation.' + rule.key) }}
              </li>
            </ul>
          </div>
        </template>

        <!-- 记住账号/密码（仅登录）+ 忘记密码 -->
        <div v-if="mode === 'login'" class="login__remember">
          <label class="login__check">
            <input v-model="rememberAccount" type="checkbox" />
            <span>{{ t('login.rememberAccount') }}</span>
          </label>
          <label class="login__check">
            <input v-model="rememberPassword" type="checkbox" @change="onRememberPasswordToggle" />
            <span>{{ t('login.rememberPassword') }}</span>
          </label>
          <button type="button" class="login__forgot" @click="switchMode('forgot')">
            {{ t('login.forgotLink') }}
          </button>
        </div>

        <!-- 协议同意（登录 / 注册 / 忘记密码共用一套文案，均需人为勾选） -->
        <AgreementCheckbox v-model="agreed" />
        <p v-if="agreeError" class="login__field-error" role="alert">{{ agreeError }}</p>

        <button type="submit" class="login__submit" :disabled="loading">
          {{ loading ? t('common.loading') : submitLabel }} →
        </button>

        <!-- 注册引导（仅登录模式） -->
        <p v-if="mode === 'login'" class="login__register-hint">
          <button type="button" class="login__link" @click="switchMode('register')">
            {{ t('login.goRegister') }}
          </button>
        </p>
      </form>

      <!-- 第三方登录（表单第二优先：分隔线 + 品牌图标行，仅展示后端配置中已就绪的 provider） -->
      <div v-if="mode === 'login' && oauthProviders.length && !resumeAccount" class="login__oauth">
        <div class="login__divider">
          <span>{{ t('login.oauthDivider') }}</span>
        </div>
        <div class="login__oauth-row">
          <button
            v-for="p in oauthProviders"
            :key="p.key"
            type="button"
            class="login__oauth-mini"
            :disabled="!!oauthLoading"
            :aria-label="oauthLabel(p)"
            @click="handleOauth(p)"
          >
            <span class="login__oauth-mini-ico" :style="{ color: oauthBrandColor(p.key) }">
              <YfbIcon :name="`brand:${p.key}`" :size="16" aria-hidden="true" />
            </span>
            <span>{{ oauthLabel(p) }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 滑块安全验证弹层(发送邮箱验证码前) -->
    <SliderCaptcha ref="captchaRef" @success="sendCodeWithCaptcha" />
  </section>
</template>

<style scoped>
.login {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(2rem, 6vw, 4.5rem) 1rem 4rem;
  min-height: calc(100vh - var(--yfb-header-h, 72px));
}

.login__card {
  width: 100%;
  max-width: 26rem;
  padding: var(--yfb-sp-7) var(--yfb-sp-6);
  background: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: 0 0 24px var(--yfb-glow), 0 16px 48px var(--yfb-drop);
  backdrop-filter: blur(14px);
}

.login__brand {
  text-align: center;
  margin-bottom: var(--yfb-sp-3);
}

/* 返回登录（注册/忘记密码顶部入口） */
.login__back {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  margin-bottom: var(--yfb-sp-5);
  padding: 0;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-ink-soft);
  background: none;
  border: none;
  cursor: pointer;
  transition: color var(--yfb-anim-fast);
}

.login__back:hover {
  color: var(--yfb-accent);
}

/* 注册引导（登录模式表单下方） */
.login__register-hint {
  margin: var(--yfb-sp-5) 0 0;
  text-align: center;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}

.login__form {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.login__label {
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-ink-soft);
}

.login__input {
  width: 100%;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  font-size: var(--yfb-fs-base);
  color: var(--yfb-ink);
  background: var(--yfb-bg);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  transition: border-color var(--yfb-anim-fast), box-shadow var(--yfb-anim-fast);
}

.login__input:focus {
  outline: none;
  border-color: var(--yfb-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--yfb-accent) 25%, transparent);
}

.login__input--error {
  border-color: var(--yfb-signal-danger);
}

.login__field-error {
  margin-top: calc(-1 * var(--yfb-sp-2));
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-signal-danger);
}

.login__code-row {
  display: flex;
  gap: var(--yfb-sp-3);
}

.login__code-input {
  flex: 1;
}

.login__code-btn {
  flex-shrink: 0;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-accent);
  background: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  transition: filter var(--yfb-anim-fast);
}

.login__code-btn:hover:not(:disabled) {
  filter: brightness(1.2);
}

.login__code-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login__inline-link {
  align-self: flex-end;
  margin-top: calc(-1 * var(--yfb-sp-2));
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-accent);
  background: none;
  border: none;
  cursor: pointer;
}

.login__submit {
  margin-top: var(--yfb-sp-2);
  padding: var(--yfb-sp-4);
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-base);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--yfb-on-accent);
  background-image: var(--yfb-grad-cta);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  box-shadow: 0 0 18px var(--yfb-glow), 0 8px 32px var(--yfb-drop);
  cursor: pointer;
  transition: filter var(--yfb-anim-fast), box-shadow var(--yfb-anim-fast);
}

.login__submit:hover:not(:disabled) {
  filter: brightness(1.1);
  box-shadow: 0 0 28px var(--yfb-glow), 0 12px 40px var(--yfb-drop);
}

.login__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 「网页端已登录」确认卡（ADR-0042）：显式选择沿用当前账号 / 更换账号 */
.login__resume {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  margin-top: var(--yfb-sp-5);
}

.login__resume-title {
  margin: 0;
  font-size: var(--yfb-fs-base);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--yfb-fg);
}

.login__resume-account {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-fg-muted);
  word-break: break-all;
}

.login__resume-switch {
  justify-content: center;
}

.login__remember {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-4);
  align-items: center;
}

.login__check {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
  cursor: pointer;
  user-select: none;
}

.login__check input[type='checkbox'] {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  accent-color: var(--yfb-accent);
  cursor: pointer;
}

.login__strength {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
}

.login__strength-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.login__strength-title {
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}

.login__strength-label {
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
}

.login__strength-label--weak { color: var(--yfb-signal-danger); }
.login__strength-label--medium { color: var(--yfb-signal-warning); }
.login__strength-label--strong { color: var(--yfb-signal-ok); }

.login__strength-track {
  height: 6px;
  border-radius: var(--yfb-radius-sm);
  background: var(--yfb-surface-2);
  overflow: hidden;
}

.login__strength-fill {
  height: 100%;
  transition: width var(--yfb-anim-fast), background var(--yfb-anim-fast);
}

.login__strength-fill--weak { background: var(--yfb-signal-danger); }
.login__strength-fill--medium { background: var(--yfb-signal-warning); }
.login__strength-fill--strong { background: var(--yfb-signal-ok); }

.login__strength-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--yfb-sp-1) var(--yfb-sp-3);
}

.login__strength-item {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}

.login__strength-item--ok {
  color: var(--yfb-signal-ok);
}

.login__strength-mark {
  flex-shrink: 0;
}

.login__link {
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  color: var(--yfb-accent);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
}

.login__link:hover {
  text-decoration: underline;
}

/* 忘记密码（登录模式，靠右） */
.login__forgot {
  margin-left: auto;
  padding: 0;
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-accent);
  background: none;
  border: none;
  cursor: pointer;
  transition: opacity var(--yfb-anim-fast);
}

.login__forgot:hover {
  opacity: 0.8;
  text-decoration: underline;
}

/* 第三方登录（表单第二优先：分隔线 + 品牌图标行） */
.login__oauth {
  margin-top: var(--yfb-sp-6);
}

.login__oauth-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-3);
}

.login__oauth-mini {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-2);
  flex: 1;
  min-width: max-content;
  padding: var(--yfb-sp-3);
  font-family: var(--yfb-font-ui);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-ink);
  background: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  cursor: pointer;
  transition: filter var(--yfb-anim-fast), box-shadow var(--yfb-anim-fast), border-color var(--yfb-anim-fast), transform var(--yfb-anim-fast);
}

.login__oauth-mini-ico {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--yfb-radius-sm);
  background: color-mix(in srgb, currentColor 10%, transparent);
  border: 1px solid color-mix(in srgb, currentColor 22%, transparent);
}

.login__oauth-mini:hover:not(:disabled) {
  filter: brightness(1.12);
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow-soft);
  transform: translateY(-1px);
}

.login__oauth-mini:focus-visible {
  outline: 2px solid var(--yfb-focus);
  outline-offset: 2px;
  border-color: var(--yfb-accent);
}

.login__oauth-mini:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 分隔线（表单 / 第三方登录过渡） */
.login__divider {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  margin-bottom: var(--yfb-sp-4);
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}

.login__divider::before,
.login__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--yfb-divider);
}

/* 移动端：输入字号提到 16px，避免 iOS Safari 聚焦缩放 */
@media (max-width: 640px) {
  .login__input {
    font-size: 1rem;
  }

  .login__card {
    padding: var(--yfb-sp-6) var(--yfb-sp-5);
  }
}
</style>
